import { useState } from 'react';
import type { ReactNode, TransitionEvent } from 'react';
import { Box } from '@mui/material';

// Upper bound of the expand animation (max-width target), in plain px: the DS has no size token this
// wide (the scale stops well below). The group sizes to its content below the cap, so two or three
// 32 px row buttons never reach it; collapsed the group is 0 and the name gets every spare pixel.
const EXPANDED_MAX_WIDTH = '160px';

const transitionSx = (revealed: boolean) =>
  ({
    transition: 'opacity var(--duration-fast) ease, max-width var(--duration-fast) ease',
    // No animation, so nothing to wait for: an expanded group is unclipped straight away.
    '@media (prefers-reduced-motion: reduce)': { transition: 'none', ...(revealed ? { overflow: 'visible' } : {}) },
    // Touch has no :hover; keep the group expanded there (and unclipped), as the other reveal controls
    // stay shown.
    '@media (hover: none)': { opacity: 1, maxWidth: EXPANDED_MAX_WIDTH, overflow: 'visible', pointerEvents: 'auto' }
  }) as const;

export interface LayersRowRevealedGroupProps {
  revealed: boolean;
  children: ReactNode;
}

/**
 * The hover/focus-revealed controls of a row (e.g. open + swap on a composition row). In normal
 * flex flow, never an overlay (DB-10c): collapsed to zero width at rest, expanded to its natural
 * width when revealed, so the flexible name column truncates before it and is never overpainted.
 * Deliberately not display: none / visibility: hidden: the controls stay in the tab order, so
 * keyboard focus can reveal the group.
 *
 * Clipping: `overflow: hidden` is what makes the collapsed group take no width, and it also keeps
 * the buttons inside the growing box while the width animates (otherwise they would paint over the
 * name before the box has grown). It is released only once the expand transition has ended (or at
 * once with reduced motion / on touch), when the 32 px box is as tall as the DS buttons and neither
 * they nor their focus ring (2 px outline + 2 px offset) are cut off.
 */
export function LayersRowRevealedGroup({ revealed, children }: LayersRowRevealedGroupProps) {
  const [settled, setSettled] = useState({ revealed, done: false });
  // Reset when the reveal state flips (render-time derivation, no effect).
  if (settled.revealed !== revealed) setSettled({ revealed, done: false });
  const unclipped = revealed && settled.done;

  const handleTransitionEnd = (event: TransitionEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if ((event.propertyName ?? 'max-width') !== 'max-width') return;
    setSettled({ revealed, done: true });
  };

  return (
    <Box
      onTransitionEnd={handleTransitionEnd}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flex: '0 0 auto',
        height: 'var(--size-32)',
        overflow: unclipped ? 'visible' : 'hidden',
        pointerEvents: revealed ? 'auto' : 'none',
        maxWidth: revealed ? EXPANDED_MAX_WIDTH : 0,
        opacity: revealed ? 1 : 0,
        ...transitionSx(revealed)
      }}
    >
      {children}
    </Box>
  );
}
